import { Font, StyleSheet } from "@react-pdf/renderer";
import theme from "styles/theme/mui";
import { themes } from "styles/theme/themes";

const PDF_FONT = "NotoSansHebrew";
const PDF_TEXT = themes.light.text;
const PDF_MUTED = theme.palette.text.primary;
const PDF_TOP_CARD_BG = `${theme.palette.grey[400]}80`;
const PDF_TABLE_HEADER_BG = theme.palette.grey[400];
const PDF_FUEL_INFO_BG = theme.palette.background.subtle;
const PDF_PAPER = theme.palette.background.paper;
const PDF_BORDER = "#E2E8F0";

Font.register({
  family: PDF_FONT,
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/noto-sans-hebrew@5.2.5/hebrew-400-normal.ttf",
      fontWeight: 400,
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/noto-sans-hebrew@5.2.5/hebrew-500-normal.ttf",
      fontWeight: 500,
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/noto-sans-hebrew@5.2.5/hebrew-600-normal.ttf",
      fontWeight: 600,
    },
  ],
});

const pdfTableShell = {
  width: "100%",
  borderWidth: 1,
  borderColor: PDF_BORDER,
  borderRadius: 4,
  overflow: "hidden" as const,
};

const pdfHeaderRow = {
  flexDirection: "row" as const,
  direction: "ltr" as const,
  height: 52,
  backgroundColor: PDF_TABLE_HEADER_BG,
  borderBottomWidth: 1,
  borderBottomColor: PDF_BORDER,
  alignItems: "center" as const,
};

const pdfHeaderCell = {
  fontFamily: PDF_FONT,
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: 1.5,
  color: PDF_MUTED,
  paddingHorizontal: 16,
  textAlign: "right" as const,
};

const pdfDataCell = {
  fontFamily: PDF_FONT,
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: 1.5,
  color: PDF_TEXT,
  textAlign: "right" as const,
};

const pdfBodyRow = {
  flexDirection: "row" as const,
  direction: "ltr" as const,
  backgroundColor: PDF_PAPER,
  borderBottomWidth: 1,
  borderBottomColor: PDF_BORDER,
};

const pdfMetaLabel = {
  fontFamily: PDF_FONT,
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: 1.5,
  color: PDF_TEXT,
  textAlign: "right" as const,
  flexShrink: 0,
};

const pdfMetaValue = {
  fontFamily: PDF_FONT,
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: 1.5,
  color: PDF_MUTED,
  textAlign: "left" as const,
  flex: 1,
  marginRight: 12,
};

const pdfSectionTitle = {
  fontSize: 12,
  fontWeight: 600,
  color: PDF_TEXT,
};

export const pdfStyles = StyleSheet.create({
  page: {
    fontFamily: PDF_FONT,
    backgroundColor: PDF_PAPER,
    paddingTop: 32,
    paddingBottom: 32,
    paddingHorizontal: 28,
    direction: "ltr",
  },
  mainTitle: {
    fontFamily: PDF_FONT,
    fontSize: 20,
    fontWeight: 600,
    color: PDF_TEXT,
    textAlign: "center",
    direction: "rtl",
    marginBottom: 24,
  },
  topRow: {
    flexDirection: "row",
    direction: "ltr",
    gap: 16,
    marginBottom: 16,
    alignItems: "flex-start",
  },
  halfCard: { flex: 1, minWidth: 0 },
  cardTitle: {
    fontFamily: PDF_FONT,
    fontSize: "14px",
    fontWeight: 600,
    color: PDF_TEXT,
    textAlign: "right",
    direction: "rtl",
    marginBottom: 8,
  },
  topInfoCardBody: {
    backgroundColor: PDF_TOP_CARD_BG,
    borderRadius: 4,
    height: 168,
    paddingHorizontal: 14,
  },
  topInfoFieldRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: 42,
    width: "100%",
  },
  fieldLabel: {
    fontFamily: PDF_FONT,
    fontSize: "14px",
    fontWeight: 500,
    color: PDF_MUTED,
    textAlign: "right",
    flex: 1,
    marginLeft: 12,
  },
  fieldValue: {
    fontFamily: PDF_FONT,
    fontSize: "14px",
    fontWeight: 400,
    color: PDF_TEXT,
    textAlign: "right",
    flex: 1,
    marginRight: 12,
  },
  technicalSpecRow: {
    flexDirection: "row",
    direction: "ltr",
    alignItems: "center",
    height: 42,
    width: "100%",
    gap: 24,
  },
  technicalSpecField: {
    flex: 1,
    minWidth: 0,
    height: 42,
    justifyContent: "center",
  },
  diagramSection: {
    width: "100%",
    marginTop: 16,
    backgroundColor: PDF_PAPER,
    borderRadius: 4,
    padding: 12,
  },
  diagramImage: {
    width: "100%",
    maxHeight: 280,
    objectFit: "contain",
  },
  pdfTable: pdfTableShell,
  pdfTableHeaderRow: pdfHeaderRow,
  pdfTableBodyRow: pdfBodyRow,
  pdfTableBodyRowLast: { borderBottomWidth: 0 },
  pdfTableHeaderCell: { flex: 1, ...pdfHeaderCell },
  pdfTableHeaderCellWide: { flex: 2.2, ...pdfHeaderCell },
  pdfTableHeaderCellCompact: { flex: 1, ...pdfHeaderCell, paddingHorizontal: 6 },
  pdfTableDataCell: pdfDataCell,
  pdfTableValueCell: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    ...pdfDataCell,
    direction: "ltr",
  },
  pdfTableValueCellLg: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 16,
    ...pdfDataCell,
  },
  pdfTableValueCellLgLtr: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 16,
    ...pdfDataCell,
    direction: "ltr",
  },
  pdfTableDetailCell: {
    flex: 2.2,
    paddingVertical: 12,
    paddingHorizontal: 10,
    ...pdfDataCell,
  },
  calculationTableDetailCellWrap: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 16,
    justifyContent: "center",
    alignItems: "flex-end",
  },
  calculationTableDetailLabelRow: {
    flexDirection: "row",
    direction: "rtl",
    alignItems: "center",
  },
  calculationTableValueCell: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 16,
    ...pdfDataCell,
    direction: "ltr",
  },
  weighingNotesSection: {
    width: "100%",
    marginTop: 16,
    height: 104,
    backgroundColor: PDF_TABLE_HEADER_BG,
    borderRadius: 4,
    paddingHorizontal: 14,
    overflow: "hidden",
  },
  weighingNoteRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    height: 52,
    width: "100%",
  },
  pdfMetaLabel: pdfMetaLabel,
  pdfMetaValue: pdfMetaValue,
  pdfMetaValueLtr: { ...pdfMetaValue, direction: "ltr" },
  dualTablesRow: {
    flexDirection: "row",
    direction: "ltr",
    gap: 12,
    marginTop: 16,
    alignItems: "flex-start",
  },
  hydraulicWallTableWrap: { flexShrink: 0, width: 192 },
  weightDistributionTableWrap: { flex: 1, minWidth: 0 },
  pdfSectionTitleLeft: { ...pdfSectionTitle, textAlign: "left", marginBottom: 8 },
  pdfTableTitleRowRight: {
    flexDirection: "row",
    direction: "ltr",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "100%",
    marginBottom: 8,
    gap: 4,
  },
  pdfSectionTitle: pdfSectionTitle,
  pdfSectionTitleLtr: { ...pdfSectionTitle, direction: "ltr" },
  pdfFooterRow: {
    flexDirection: "row",
    direction: "ltr",
    justifyContent: "flex-end",
    alignItems: "center",
    width: "100%",
    marginTop: 12,
    gap: 4,
  },
  pdfFooterText: { fontSize: 11, fontWeight: 400, color: PDF_MUTED },
  pdfFooterLtrText: { fontSize: 11, fontWeight: 400, color: PDF_MUTED, direction: "ltr" },
  manualWeighingSection: { width: "100%", marginTop: 24 },
  manualWeighingTitle: {
    fontFamily: PDF_FONT,
    fontSize: 20,
    fontWeight: 600,
    lineHeight: 0.8,
    color: PDF_TEXT,
    textAlign: "center",
    direction: "rtl",
    marginBottom: 12,
  },
  manualWeighingFillerLabel: {
    ...pdfSectionTitle,
    textAlign: "right",
    marginBottom: 8,
  },
  fuelInfoSection: { width: "100%", marginTop: 16 },
  fuelInfoOuterWrap: {
    width: "100%",
    height: 292,
    backgroundColor: PDF_FUEL_INFO_BG,
    borderRadius: 4,
    padding: 16,
    flexDirection: "row",
    direction: "ltr",
    alignItems: "center",
    gap: 24,
    overflow: "hidden",
  },
  fuelInfoFieldsWrap: {
    width: 287,
    height: 260,
    backgroundColor: PDF_PAPER,
    borderRadius: 4,
    flexShrink: 0,
    overflow: "hidden",
  },
  fuelInfoFieldRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: 287,
    height: 52,
    paddingHorizontal: 16,
  },
  fuelInfoChartColumn: {
    flexShrink: 0,
    width: 168,
    alignItems: "center",
    justifyContent: "center",
  },
  fuelInfoChartImage: { width: 168, height: 168 },
});
